import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Extension } from "@tiptap/core";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { API_BASE } from "../config";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HIGHLIGHT_COLORS = [
  { label: "Yellow", color: "#fde047" },
  { label: "Mint",   color: "#4ade80" },
  { label: "Blue",   color: "#60a5fa" },
  { label: "Pink",   color: "#f472b6" },
  { label: "Peach",  color: "#fb923c" },
];

const TabIndent = Extension.create({
  name: "tabIndent",
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        if (editor.isActive("listItem")) {
          return editor.commands.sinkListItem("listItem");
        }
        editor.commands.insertContent("  ");
        return true;
      },
    };
  },
});

/** Parse freeformContent: either TipTap JSON or legacy plain text */
function parseEditorContent(raw) {
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.type === "doc") return parsed;
  } catch {
    // Not JSON — treat as plain text paragraph
  }
  // Wrap legacy plain text in a doc node
  return {
    type: "doc",
    content: [{ type: "paragraph", content: [{ type: "text", text: raw }] }],
  };
}

function generatePrintHTML(notes, editorHTML) {
  const citations = notes.citations
    .map(
      (c) => `
      <div class="citation">
        ${c.wordContext ? `<blockquote>…${c.wordContext}…</blockquote>` : ""}
        <p>${c.text.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
      </div>`
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Notes — ${notes.documentTitle || "Document"}</title>
  <style>
    body { font-family: Georgia, "Times New Roman", serif; max-width: 680px; margin: 48px auto; color: #222; line-height: 1.6; }
    h1 { font-size: 1.4rem; margin-bottom: 4px; }
    .subtitle { color: #888; font-size: 0.85rem; margin-bottom: 32px; }
    h2 { font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #555; margin-top: 36px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    blockquote { border-left: 3px solid #ccc; margin: 0 0 6px 0; padding: 2px 12px; color: #666; font-style: italic; font-size: 0.85rem; }
    .citation { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #eee; }
    .citation p { margin: 4px 0; font-size: 0.95rem; }
    .freeform h1 { font-size: 1.3rem; margin-top: 24px; }
    .freeform h2 { font-size: 1.1rem; }
    .freeform h3 { font-size: 1rem; }
    .freeform ul, .freeform ol { padding-left: 24px; }
    .freeform li { margin: 3px 0; }
    .freeform input[type=checkbox] { margin-right: 6px; }
    mark { padding: 1px 2px; border-radius: 2px; }
    @media print { body { margin: 24px; } }
  </style>
</head>
<body>
  <h1>Notes — ${notes.documentTitle || "Document"}</h1>
  <p class="subtitle">Spritz Reader export</p>
  ${notes.citations.length > 0 ? `<h2>Reading Annotations</h2>${citations}` : ""}
  ${editorHTML ? `<h2>Document Notes</h2><div class="freeform">${editorHTML}</div>` : ""}
</body>
</html>`;
}

function generateMarkdown(notes, editorText) {
  const lines = [`# Notes — ${notes.documentTitle || "Document"}`, ""];

  if (notes.citations.length > 0) {
    lines.push("## Reading Annotations", "");
    notes.citations.forEach((c) => {
      if (c.wordContext) lines.push(`> …${c.wordContext}…`, "");
      lines.push(c.text, "");
    });
  }

  if (editorText?.trim()) {
    lines.push("## Document Notes", "", editorText, "");
  }

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

function ToolbarBtn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`notes-toolbar-btn ${active ? "notes-toolbar-btn-active" : ""}`}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <span className="notes-toolbar-sep" aria-hidden />;
}

// ---------------------------------------------------------------------------
// Rich editor toolbar
// ---------------------------------------------------------------------------

function EditorToolbar({ editor }) {
  if (!editor) return null;

  const toggleHighlight = (color) => {
    if (editor.isActive("highlight", { color })) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().toggleHighlight({ color }).run();
    }
  };

  return (
    <div className="notes-toolbar" role="toolbar" aria-label="Formatting">
      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        title="Bold"
      ><b>B</b></ToolbarBtn>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        title="Italic"
      ><i>I</i></ToolbarBtn>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
        title="Strikethrough"
      ><s>S</s></ToolbarBtn>

      <ToolbarSep />

      {[1, 2, 3].map((level) => (
        <ToolbarBtn
          key={level}
          onClick={() => {
            if (editor.isActive("heading", { level })) {
              // Already this heading level — toggle back to paragraph
              editor.chain().focus().setParagraph().run();
              return;
            }
            const { state } = editor;
            const { from } = state.selection;
            const $from = state.doc.resolve(Math.max(1, Math.min(from, state.doc.content.size)));
            const inTextBlock = $from.depth > 0 && $from.parent.isTextblock;
            const currentBlockEmpty = !inTextBlock || $from.parent.content.size === 0;
            if (currentBlockEmpty) {
              // Empty line — convert it to heading in place, cursor stays inside
              editor.chain().focus().setHeading({ level }).run();
            } else {
              // Line has content — insert a new empty heading after it
              editor.chain()
                .focus()
                .setTextSelection($from.end())
                .splitBlock()
                .setHeading({ level })
                .run();
            }
          }}
          active={editor.isActive("heading", { level })}
          title={`Heading ${level}`}
        >H{level}</ToolbarBtn>
      ))}

      <ToolbarSep />

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        title="Bullet list"
      >•—</ToolbarBtn>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        title="Numbered list"
      >1.</ToolbarBtn>

      <ToolbarBtn
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        active={editor.isActive("taskList")}
        title="Task list"
      >☐</ToolbarBtn>

      <ToolbarSep />

      <ToolbarBtn
        onClick={() => editor.chain().focus().liftListItem("listItem").run()}
        disabled={!editor.can().liftListItem("listItem")}
        title="Decrease indent"
      >⇤</ToolbarBtn>

      <ToolbarBtn
        onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
        disabled={!editor.can().sinkListItem("listItem")}
        title="Increase indent"
      >⇥</ToolbarBtn>

      <ToolbarSep />

      {HIGHLIGHT_COLORS.map(({ label, color }) => (
        <button
          key={color}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); toggleHighlight(color); }}
          title={`Highlight: ${label}`}
          aria-label={`Highlight ${label}`}
          className={`notes-toolbar-color ${editor.isActive("highlight", { color }) ? "notes-toolbar-color-active" : ""}`}
          style={{ background: color }}
        />
      ))}

      <ToolbarBtn
        onClick={() => editor.chain().focus().unsetHighlight().run()}
        title="Clear highlight"
      >✕</ToolbarBtn>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function NotesPanel({ notes, onNotesChange, fileId, pdfUrl, pageBreaks, accessToken }) {
  const [activeTab, setActiveTab] = useState("citations");
  const [downloadingMode, setDownloadingMode] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportMenuPos, setExportMenuPos] = useState({ top: 0, right: 0 });
  const exportMenuRef = useRef(null);
  const exportTriggerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      TabIndent,
    ],
    content: parseEditorContent(notes.freeformContent),
    onUpdate: ({ editor }) => {
      onNotesChange({ freeformContent: JSON.stringify(editor.getJSON()) });
    },
  });

  // Sync editor content when notes.freeformContent changes from outside
  // (e.g. migration on first load) — but only if the editor is in the default empty state
  // (one empty paragraph). Do NOT sync if the user has already started editing (e.g. set a heading),
  // because setContent resets cursor position and causes it to land outside the new block.
  useEffect(() => {
    if (!editor) return;
    const { doc } = editor.state;
    const isDefaultEmpty = doc.childCount === 1
      && doc.firstChild?.type.name === 'paragraph'
      && doc.firstChild?.content.size === 0;
    if (isDefaultEmpty && notes.freeformContent) {
      editor.commands.setContent(parseEditorContent(notes.freeformContent));
    }
  }, [editor, notes.freeformContent]);

  // Close export menu on outside click (mouse and touch)
  useEffect(() => {
    if (!showExportMenu) return;
    function onOutsideClick(e) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("touchstart", onOutsideClick);
    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("touchstart", onOutsideClick);
    };
  }, [showExportMenu]);

  const handleDeleteCitation = useCallback(
    (id) => onNotesChange({ citations: notes.citations.filter((c) => c.id !== id) }),
    [notes.citations, onNotesChange]
  );

  const handleDownload = useCallback(async (mode) => {
    setShowExportMenu(false);
    setDownloadError(null);

    if (mode === "markdown") {
      const md = generateMarkdown(notes, editor?.getText() || "");
      const blob = new Blob([md], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(notes.documentTitle || "document").replace(/\.pdf$/i, "")}_notes.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }
    if (mode === "print") {
      const html = generatePrintHTML(notes, editor?.getHTML() || "");
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) { URL.revokeObjectURL(url); return; }
      win.addEventListener("load", () => {
        win.focus();
        win.print();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      });
      return;
    }

    setDownloadingMode(mode);
    try {
      const headers = { "Content-Type": "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const res = await fetch(`${API_BASE}/api/files/annotate`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          file_id: fileId || "",
          pdf_url: pdfUrl || null,
          pageBreaks: pageBreaks || [],
          documentTitle: notes.documentTitle || "",
          freeformNotes: editor?.getText()?.trim() || "",
          download_mode: mode,
          include_summary: mode !== "citations_only",
          citations: notes.citations.map(({ text, wordIndex, wordContext }) => ({
            text,
            wordIndex,
            wordContext: wordContext || "",
          })),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const base = (notes.documentTitle || "document").replace(/\.pdf$/i, "");
      const suffix = mode === "citations_only" ? "_annotated" : mode === "notes_only" ? "_notes" : "_full_study";
      a.download = `${base}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setDownloadError(err.message || "Download failed. Please try again.");
    } finally {
      setDownloadingMode(null);
    }
  }, [fileId, pdfUrl, notes, pageBreaks, accessToken, editor]);

  const hasNotes = !!(editor?.getText()?.trim());
  const hasCitations = notes.citations.length > 0;
  const hasFile = !!fileId;

  return (
    <div className="notes-panel">
      {/* Tab + export bar */}
      <div className="notes-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "citations"}
          className={`notes-tab ${activeTab === "citations" ? "notes-tab-active" : ""}`}
          onClick={() => setActiveTab("citations")}
        >
          Annotations {notes.citations.length > 0 && `(${notes.citations.length})`}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "document"}
          className={`notes-tab ${activeTab === "document" ? "notes-tab-active" : ""}`}
          onClick={() => setActiveTab("document")}
        >
          Document
        </button>
        <div className="notes-tab-spacer" />
        <div className="notes-export-wrap">
          <button
            ref={exportTriggerRef}
            type="button"
            className="notes-export-trigger"
            onClick={() => {
              if (!showExportMenu && exportTriggerRef.current) {
                const r = exportTriggerRef.current.getBoundingClientRect();
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                  setExportMenuPos({ top: null, right: null, mobile: true });
                } else {
                  const menuHeight = 320; // approximate
                  const spaceBelow = window.innerHeight - r.bottom;
                  const openUpward = spaceBelow < menuHeight && r.top > menuHeight;
                  setExportMenuPos({
                    top: openUpward ? null : r.bottom + 6,
                    bottom: openUpward ? window.innerHeight - r.top + 6 : null,
                    right: window.innerWidth - r.right,
                    mobile: false,
                  });
                }
              }
              setShowExportMenu(v => !v);
            }}
            disabled={!!downloadingMode}
            aria-haspopup="true"
            aria-expanded={showExportMenu}
          >
            {downloadingMode ? "Downloading…" : "Export ▾"}
          </button>
          {showExportMenu && createPortal(
            <div
              ref={exportMenuRef}
              className="notes-export-menu"
              role="menu"
              style={exportMenuPos.mobile
                ? { position: "fixed", left: 12, right: 12, bottom: "calc(56px + env(safe-area-inset-bottom, 0px) + 8px)", top: "auto", width: "auto", minWidth: 0 }
                : { position: "fixed", top: exportMenuPos.top ?? "auto", bottom: exportMenuPos.bottom ?? "auto", right: exportMenuPos.right, left: "auto" }
              }
            >
              <div className="notes-export-section-label">PDF</div>
              <button
                type="button"
                role="menuitem"
                className="notes-export-option"
                onClick={() => handleDownload("citations_only")}
                disabled={!hasFile || !hasCitations}
                title={!hasFile ? "Upload a PDF first" : !hasCitations ? "Add annotations first" : ""}
              >
                <span className="notes-export-option-title">Annotated PDF</span>
                <span className="notes-export-option-desc">Original document with annotations in the margins</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="notes-export-option"
                onClick={() => handleDownload("notes_only")}
                disabled={!hasNotes}
                title={!hasNotes ? "Write some notes first" : ""}
              >
                <span className="notes-export-option-title">Notes PDF</span>
                <span className="notes-export-option-desc">Your notes as a standalone PDF</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="notes-export-option"
                onClick={() => handleDownload("full")}
                disabled={!hasFile || !hasCitations}
                title={!hasFile ? "Upload a PDF first" : !hasCitations ? "Add annotations first" : ""}
              >
                <span className="notes-export-option-title">Full Study PDF</span>
                <span className="notes-export-option-desc">Notes + annotations list + annotated document</span>
              </button>
              <div className="notes-export-divider" />
              <div className="notes-export-section-label">Other</div>
              <button type="button" role="menuitem" className="notes-export-option" onClick={() => handleDownload("markdown")}>
                <span className="notes-export-option-title">Markdown (.md)</span>
                <span className="notes-export-option-desc">Plain text file of your notes</span>
              </button>
              <button type="button" role="menuitem" className="notes-export-option" onClick={() => handleDownload("print")}>
                <span className="notes-export-option-title">Print</span>
                <span className="notes-export-option-desc">Open print dialog</span>
              </button>
            </div>
          , document.body)}
        </div>
      </div>
      {downloadError && (
        <div className="notes-annotate-error">{downloadError}</div>
      )}

      {/* Citations tab */}
      {activeTab === "citations" && (
        notes.citations.length === 0 ? (
          <div className="notes-citations-empty">
            Pause the reader and type a note to save an annotation here.
          </div>
        ) : (
          <>
            <div className="notes-citations-list" role="list">
              {notes.citations.map((c) => (
                <div key={c.id} className="notes-citation-card" role="listitem">
                  {c.wordContext && (
                    <div className="notes-citation-context">…{c.wordContext}…</div>
                  )}
                  <div className="notes-citation-text">{c.text}</div>
                  <button
                    type="button"
                    className="notes-citation-delete"
                    onClick={() => handleDeleteCitation(c.id)}
                    aria-label="Delete this annotation"
                    title="Delete"
                  >✕</button>
                </div>
              ))}
            </div>
          </>
        )
      )}

      {/* Document tab — rich editor */}
      {activeTab === "document" && (
        <div className="notes-editor-wrap">
          <EditorToolbar editor={editor} />
          <EditorContent editor={editor} className="notes-editor-content" />
        </div>
      )}
    </div>
  );
}
